import React, { useState } from 'react';
import { useAuthContext } from "../auth/LauncherAuthContext";
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import { Card, Collapse, List, Avatar, Tag, Space, Typography, Divider, Button, Tooltip, Popconfirm, Modal, Form, Switch, Select, Input, Checkbox, Row, Col, message } from "antd";
import { GlobalOutlined, FacebookOutlined, InstagramOutlined, LinkOutlined, UserOutlined, PhoneOutlined, MailOutlined, CheckCircleOutlined, CloseCircleOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, PlusOutlined, BookOutlined } from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useNavigate } from "react-router";
import { AddressInput } from "../maps/components/AddressInput";
import { Place } from "../maps/types";
import { PlaySpaceDto } from '../../commons/data/types';

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

export const OrganizerHome: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn shouldBeOrganizer={true}>
                <OrganizerHomeContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

const OrganizerHomeContent: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const organizer = user?.organizer;

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPlaySpace, setEditingPlaySpace] = useState<PlaySpaceDto | null>(null);
    const [form] = Form.useForm();
    const isOnline = Form.useWatch('online', form);

    const { data: playSpaces, refetch } = useQuery<PlaySpaceDto[]>(
        'organizer-playspaces',
        () => axios.get('/api/organizer/playspaces').then(res => res.data),
        { enabled: !!organizer }
    );

    if (!organizer) {
        return null;
    }

    const { name, description, logoUrl, website, facebook, instagram, tiktok, gameMasters = [] } = organizer;

    const handleEdit = (ps: PlaySpaceDto) => {
        setEditingPlaySpace(ps);
        const initialPlace: Place | undefined = !ps.online ? {
            id: ps.addressGmapsId!,
            googleMapsUri: ps.addressGmapsUrl!,
            formattedAddress: ps.addressGmapsFormatted!,
            location: {
                latitude: ps.addressGmapsLatitude!,
                longitude: ps.addressGmapsLongitude!,
            },
            postalAddress: {
                locality: ps.addressGmapsLocality!,
                regionCode: ps.addressGmapsRegionCode!,
            }
        } : undefined;

        form.setFieldsValue({
            ...ps,
            place: initialPlace
        });
        setModalOpen(true);
    };

    const handleAdd = () => {
        setEditingPlaySpace(null);
        form.resetFields();
        form.setFieldsValue({
            online: false,
            wheelchairAccess: false,
            canEatIn: false,
            canDrinkIn: false,
            canSmoke: false,
            publicSpace: false,
            allowedToOperate: true
        });
        setModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        try {
            await axios.delete(`/api/organizer/playspaces/${id}`);
            message.success('Play space deleted successfully');
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || 'Failed to delete play space');
        }
    };

    const handleSubmit = async (values: any) => {
        try {
            const payload: PlaySpaceDto = {
                online: !!values.online,
                onlinePlatform: values.online ? values.onlinePlatform : undefined,
                addressGmapsId: !values.online ? values.place?.id : undefined,
                addressGmapsUrl: !values.online ? values.place?.googleMapsUri : undefined,
                addressGmapsFormatted: !values.online ? values.place?.formattedAddress : undefined,
                addressGmapsLatitude: !values.online ? values.place?.location?.latitude : undefined,
                addressGmapsLongitude: !values.online ? values.place?.location?.longitude : undefined,
                addressGmapsLocality: !values.online ? values.place?.postalAddress?.locality : undefined,
                addressGmapsRegionCode: !values.online ? values.place?.postalAddress?.regionCode : undefined,
                accessInfo: !values.online ? values.accessInfo : undefined,
                wheelchairAccess: !values.online && !!values.wheelchairAccess,
                canEatIn: !values.online && !!values.canEatIn,
                canDrinkIn: !values.online && !!values.canDrinkIn,
                canSmoke: !values.online && !!values.canSmoke,
                publicSpace: !values.online && !!values.publicSpace,
                allowedToOperate: !values.online && !!values.allowedToOperate,
            };

            if (editingPlaySpace?.id) {
                await axios.put(`/api/organizer/playspaces/${editingPlaySpace.id}`, payload);
                message.success('Play space updated successfully');
            } else {
                await axios.post('/api/organizer/playspaces', payload);
                message.success('Play space created successfully');
            }
            setModalOpen(false);
            refetch();
        } catch (err: any) {
            message.error(err.response?.data || 'Failed to save play space');
        }
    };

    return (
        <>
            <Card
                style={{ ...launcherCard, maxWidth: '800px', margin: '40px auto 20px' }}
                cover={
                    logoUrl ? (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '24px', background: 'rgba(255, 255, 255, 0.05)' }}>
                            <Avatar size={120} src={logoUrl} icon={<UserOutlined />} style={{ border: '3px solid #1890ff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }} />
                        </div>
                    ) : null
                }
            >
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <Title level={2} style={{ marginBottom: '8px' }}>{name}</Title>
                    <Paragraph style={{ fontSize: '16px', color: '#555', maxWidth: '600px', margin: '0 auto 16px' }}>
                        {description}
                    </Paragraph>

                    <Space size="middle" wrap style={{ justifyContent: 'center' }}>
                        {website?.url && (
                            <a href={website.url} target="_blank" rel="noopener noreferrer">
                                <Tag icon={<GlobalOutlined />} color="blue" style={{ padding: '4px 12px', cursor: 'pointer' }}>Website</Tag>
                            </a>
                        )}
                        {facebook?.url && (
                            <a href={facebook.url} target="_blank" rel="noopener noreferrer">
                                <Tag icon={<FacebookOutlined />} color="geekblue" style={{ padding: '4px 12px', cursor: 'pointer' }}>Facebook</Tag>
                            </a>
                        )}
                        {instagram?.url && (
                            <a href={instagram.url} target="_blank" rel="noopener noreferrer">
                                <Tag icon={<InstagramOutlined />} color="magenta" style={{ padding: '4px 12px', cursor: 'pointer' }}>Instagram</Tag>
                            </a>
                        )}
                        {tiktok?.url && (
                            <a href={tiktok.url} target="_blank" rel="noopener noreferrer">
                                <Tag icon={<LinkOutlined />} color="purple" style={{ padding: '4px 12px', cursor: 'pointer' }}>TikTok</Tag>
                            </a>
                        )}
                    </Space>

                    <div style={{ marginTop: '20px' }}>
                        <Button
                            type="primary"
                            icon={<BookOutlined />}
                            size="large"
                            onClick={() => navigate('/organizer/adventures')}
                            style={{ borderRadius: '8px' }}
                        >
                            Manage Adventures
                        </Button>
                    </div>
                </div>

                <Divider />

                <Collapse
                    ghost
                    expandIconPosition="end"
                    style={{ background: 'rgba(255, 255, 255, 0.8)', borderRadius: '8px', padding: '4px' }}
                >
                    <Panel
                        header={
                            <Space>
                                <Text strong style={{ fontSize: '16px' }}>Game Masters ({gameMasters.length})</Text>
                            </Space>
                        }
                        key="game-masters"
                    >
                        <List
                            itemLayout="horizontal"
                            dataSource={gameMasters}
                            renderItem={(gm) => (
                                <List.Item
                                    actions={[
                                        gm.verified ? (
                                            <Tag icon={<CheckCircleOutlined />} color="success">Verified</Tag>
                                        ) : (
                                            <Tag icon={<CloseCircleOutlined />} color="default">Unverified</Tag>
                                        )
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                                        title={
                                            <Space>
                                                <Text strong>{gm.name}</Text>
                                                {gm.nickname && <Text type="secondary">({gm.nickname})</Text>}
                                            </Space>
                                        }
                                        description={
                                            <Space direction="vertical" size={0}>
                                                {gm.email && (
                                                    <Text type="secondary">
                                                        <MailOutlined style={{ marginRight: '6px' }} />
                                                        {gm.email}
                                                    </Text>
                                                )}
                                                {gm.cellphone && (
                                                    <Text type="secondary">
                                                        <PhoneOutlined style={{ marginRight: '6px' }} />
                                                        {gm.cellphone}
                                                    </Text>
                                                )}
                                            </Space>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                    </Panel>

                    <Panel
                        header={
                            <Space>
                                <Text strong style={{ fontSize: '16px' }}>Play Spaces ({playSpaces?.length || 0})</Text>
                            </Space>
                        }
                        key="play-spaces"
                    >
                        <List
                            itemLayout="vertical"
                            dataSource={playSpaces}
                            renderItem={(ps) => (
                                <List.Item
                                    key={ps.id}
                                    actions={[
                                        <Tooltip title={!ps.canModify ? "Cannot edit a play space with active future sessions" : "Edit Play Space"}>
                                            <Button
                                                type="text"
                                                icon={<EditOutlined />}
                                                disabled={!ps.canModify}
                                                onClick={() => handleEdit(ps)}
                                            />
                                        </Tooltip>,
                                        <Tooltip title={!ps.canModify ? "Cannot delete a play space with active future sessions" : "Delete Play Space"}>
                                            <div>
                                                <Popconfirm
                                                    title="Are you sure you want to delete this play space?"
                                                    onConfirm={() => handleDelete(ps.id!)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                    disabled={!ps.canModify}
                                                >
                                                    <Button
                                                        type="text"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        disabled={!ps.canModify}
                                                    />
                                                </Popconfirm>
                                            </div>
                                        </Tooltip>
                                    ]}
                                >
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar
                                                icon={ps.online ? <GlobalOutlined /> : <EnvironmentOutlined />}
                                                style={{ backgroundColor: ps.online ? '#722ed1' : '#1890ff' }}
                                            />
                                        }
                                        title={
                                            <Space>
                                                <Text strong>{ps.online ? 'Online Space' : 'Physical Space'}</Text>
                                                {ps.online && <Tag color="purple">{ps.onlinePlatform}</Tag>}
                                            </Space>
                                        }
                                        description={
                                            ps.online ? (
                                                <Text type="secondary">Available platform: {ps.onlinePlatform}</Text>
                                            ) : (
                                                <Space direction="vertical" size={2}>
                                                    <Text strong type="secondary">{ps.addressGmapsFormatted}</Text>
                                                    {ps.accessInfo && <Text type="secondary"><strong>Access:</strong> {ps.accessInfo}</Text>}
                                                    <Space wrap size={[4, 8]}>
                                                        {ps.wheelchairAccess && <Tag color="blue">Wheelchair Access</Tag>}
                                                        {ps.canEatIn && <Tag color="cyan">Can Eat</Tag>}
                                                        {ps.canDrinkIn && <Tag color="geekblue">Can Drink</Tag>}
                                                        {ps.canSmoke && <Tag color="orange">Can Smoke</Tag>}
                                                        {ps.publicSpace && <Tag color="green">Public Space</Tag>}
                                                        {ps.allowedToOperate && <Tag color="gold">Allowed to Operate</Tag>}
                                                    </Space>
                                                </Space>
                                            )
                                        }
                                    />
                                </List.Item>
                            )}
                            footer={
                                <Button
                                    type="dashed"
                                    block
                                    icon={<PlusOutlined />}
                                    onClick={handleAdd}
                                    style={{ marginTop: '12px' }}
                                >
                                    Add Play Space
                                </Button>
                            }
                        />
                    </Panel>
                </Collapse>
            </Card>

            <Modal
                title={editingPlaySpace ? "Edit Play Space" : "Add Play Space"}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Save"
                cancelText="Cancel"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: '16px' }}
                >
                    <Form.Item name="online" label="Play Space Type" valuePropName="checked">
                        <Switch checkedChildren="Online" unCheckedChildren="Physical" />
                    </Form.Item>

                    {isOnline ? (
                        <Form.Item
                            name="onlinePlatform"
                            label="Online Platform"
                            rules={[{ required: true, message: 'Please select an online platform' }]}
                        >
                            <Select placeholder="Select platform">
                                <Select.Option value="DISCORD">Discord</Select.Option>
                                <Select.Option value="ROLL20">Roll20</Select.Option>
                                <Select.Option value="DNDBEYOND">DnD Beyond</Select.Option>
                            </Select>
                        </Form.Item>
                    ) : (
                        <>
                            <Form.Item
                                name="place"
                                label="Address (Google Maps Search)"
                                rules={[{ required: true, message: 'Please search and select an address' }]}
                            >
                                <AddressInput />
                            </Form.Item>
                            <Form.Item
                                name="accessInfo"
                                label="Access Info"
                                rules={[{ required: true, message: 'Please provide access info' }]}
                            >
                                <Input.TextArea placeholder="Enter access instructions" rows={3} />
                            </Form.Item>
                            <Form.Item label="Features & Options">
                                <Row gutter={[16, 16]}>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="wheelchairAccess" valuePropName="checked" noStyle>
                                            <Checkbox>Wheelchair Access</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="canEatIn" valuePropName="checked" noStyle>
                                            <Checkbox>Can Eat In</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="canDrinkIn" valuePropName="checked" noStyle>
                                            <Checkbox>Can Drink In</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="canSmoke" valuePropName="checked" noStyle>
                                            <Checkbox>Can Smoke</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="publicSpace" valuePropName="checked" noStyle>
                                            <Checkbox>Public Space</Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col xs={12} sm={8}>
                                        <Form.Item name="allowedToOperate" valuePropName="checked" noStyle>
                                            <Checkbox>Allowed to Operate</Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </>
    );
};
